import { TIME_RANGES, DEFAULT_TIMEZONE, type TimeRangeKey } from "@/config/timeRanges";

export interface ResolvedRange {
  fromIso: string;
  toIso: string;
  label: string;
  bucketSeconds: number;
}

/**
 * Resolve a TimeRangeKey + optional custom bounds against `now`.
 * The resolution is intentionally done on each call so that auto-
 * refresh always queries the freshest window.
 */
export function resolveRange(
  key: TimeRangeKey,
  customFromIso?: string,
  customToIso?: string,
  now: Date = new Date(),
): ResolvedRange {
  if (key === "custom" && customFromIso && customToIso) {
    const from = new Date(customFromIso);
    const to = new Date(customToIso);
    const seconds = Math.max(60, Math.round((to.getTime() - from.getTime()) / 1000));
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      label: `Custom (${formatDuration(seconds)})`,
      bucketSeconds: suggestedBucket(seconds),
    };
  }
  const preset = TIME_RANGES.find((r) => r.key === key) ?? TIME_RANGES[2]; // 1h
  const to = now;
  const from = new Date(now.getTime() - preset.seconds * 1000);
  return {
    fromIso: from.toISOString(),
    toIso: to.toISOString(),
    label: preset.label,
    bucketSeconds: preset.bucketSeconds,
  };
}

function suggestedBucket(totalSeconds: number): number {
  // Aim for ~60 buckets across the window.
  const raw = Math.max(30, Math.round(totalSeconds / 60));
  // Snap to friendly sizes.
  const sizes = [30, 60, 300, 900, 1800, 3600, 21600, 86400];
  return sizes.find((s) => s >= raw) ?? 86400;
}

const TZ_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const k = tz + JSON.stringify(opts);
  let f = TZ_FORMATTERS.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat("en-NZ", { timeZone: tz, ...opts });
    TZ_FORMATTERS.set(k, f);
  }
  return f;
}

export function formatTimestamp(iso: string, tz: string = DEFAULT_TIMEZONE): string {
  const d = new Date(iso);
  return fmt(tz, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).format(d);
}

export function formatTime(iso: string, tz: string = DEFAULT_TIMEZONE): string {
  return fmt(tz, {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(new Date(iso));
}

export function formatRelative(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  const diff = Math.round((now - t) / 1000);
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "ago" : "from now";
  if (abs < 5) return "just now";
  if (abs < 60) return `${abs}s ${sign}`;
  if (abs < 3600) return `${Math.round(abs / 60)}m ${sign}`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${sign}`;
  return `${Math.round(abs / 86400)}d ${sign}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round((seconds / 3600) * 10) / 10}h`;
  return `${Math.round((seconds / 86400) * 10) / 10}d`;
}
